import { Alert, Divider, Form, Input, Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { AgentSelect } from './AgentSelect'
import { SETTLEMENT_MODES } from './agentApi'
import { PHONE_MAX, PHONE_MIN, PHONE_SAFE } from './agentAccountApi'

interface AgentCreateFieldsProps {
  currency: string
  /** `user.create` — login va parol maydonlari shunga bog'liq */
  canCreateUser: boolean
  /** `agent.deposit.add` — depozit va qarz limiti shunga bog'liq */
  canFund: boolean
}

/**
 * Agent qo'shish — BITTA forma, uchta bo'lim.
 *
 * <h2>⚠️ NEGA AGENT VA RESELLER FORMALARI AJRATILDI</h2>
 * Egasi qarori (2026-08-19): «adminkada agent qo'shish bilan reseller
 * qo'shish bir xil bo'lmaydi ... tizimni qulay qilishimiz kerak, kirgan
 * odam adashib qolishi kerakmas». Ilgari ikkala marshrut ham AYNI
 * formani ochardi va u ikkalasiga ham chala mos edi: agentdan reseller
 * maydonlari, resellerdan agent maydonlari so'ralardi.
 *
 * <h2>⚠️ NIMA SO'RALMAYDI VA NEGA</h2>
 * <ul>
 *   <li><b>Turi</b> (`kind`) — marshrutning O'ZI aytadi. «Agentlar»
 *       sahifasida reseller yaratish tanlovi operatorni adashtirardi va
 *       yaratilgan tugun ro'yxatdan darhol yo'qolardi (XSB-002).</li>
 *   <li><b>Abonent amallari chegarasi</b> (`LimitKind`: o'chirish, nom
 *       almashtirish, tarif almashtirish, MAC almashtirish) — bular
 *       RESELLER ning o'z abonentlari ustidagi amallari. Agentda o'z
 *       abonenti YO'Q (ADR-029: u ixtiyoriy abonentga pul tashlaydi,
 *       tahrirlamaydi), ya'ni bu chegaralar unda hech qachon ishlamaydi
 *       — ularni so'rash bo'sh savol bo'lardi.</li>
 *   <li><b>Bloklash va holat</b> — yaratish paytida hech kim to'xtatilgan
 *       agent ochmaydi. Holat tugun tanlangandan keyin
 *       `AgentGovernance` da o'zgaradi.</li>
 *   <li><b>Komissiya qoidasi</b> — u paketga bog'lanadi va bittadan ko'p
 *       bo'ladi, ya'ni yaratish formasiga sig'maydi.</li>
 * </ul>
 *
 * ⚠️ Parol BIR MARTA kiritiladi va hech qayerda qaytarilmaydi
 * (CLAUDE.md §7). «Ko'zcha» tugmasi ham yo'q — operator yonidagi odamga
 * parolni ko'rsatib qo'ymasin (`UserFormModal` bilan bir xil qaror).
 */
export function AgentCreateFields({ currency, canCreateUser, canFund }: AgentCreateFieldsProps) {
  const { t } = useTranslation()

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('agents.account.agentIntro')}
      />

      <Typography.Text strong>{t('agents.account.whoTitle')}</Typography.Text>
      <Divider style={{ margin: '8px 0 16px' }} />

      <Form.Item
        name="displayName"
        label={t('agents.account.fullName')}
        extra={t('agents.account.fullNameHint')}
        rules={[{ required: true, message: t('agents.displayNameRequired') }]}
      >
        <Input autoFocus autoComplete="off" />
      </Form.Item>

      {/*
        ⚠️ KONTAKT `platform.contact` GA YOZILADI (ADR-031), tugunga emas.
        `contactName` — ODAM ismi, yuqoridagi `displayName` esa TUGUN nomi
        ("Toshkent-3 shaxobchasi"). Ular ko'pincha boshqa va bitta maydonga
        yig'ilsa keyin ajratib bo'lmasdi.
      */}
      <Form.Item name="contactName" label={t('agents.account.contactName')}>
        <Input autoComplete="off" />
      </Form.Item>
      {/*
        ⚠️ FORMAT MAJBURLANMAYDI (GEO §7, egasi qarori). Tizim Afg'onistonda
        ham ishlaydi. Qolgan qoida serverdagi `contact_phone_shape` ning
        aynan o'zi — uzunlik va xavfli belgi yo'qligi; usiz operator
        sababsiz 400 olardi.
      */}
      <Form.Item
        name="contactPhone"
        label={t('agents.account.contactPhone')}
        extra={t('agents.account.contactPhoneHint')}
        rules={[
          { min: PHONE_MIN, max: PHONE_MAX, message: t('agents.account.contactPhoneFormat') },
          { pattern: PHONE_SAFE, message: t('agents.account.contactPhoneFormat') },
        ]}
      >
        <Input autoComplete="off" inputMode="tel" />
      </Form.Item>
      <Form.Item name="contactNote" label={t('agents.account.contactNote')}>
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} maxLength={500} />
      </Form.Item>

      {canCreateUser ? (
        <>
          <Form.Item
            name="username"
            label={t('agents.account.login')}
            rules={[
              { required: true, message: t('agents.account.loginRequired') },
              { pattern: /^[a-zA-Z0-9._@-]{3,64}$/, message: t('agents.account.loginFormat') },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="rawPassword"
            label={t('agents.account.password')}
            extra={t('agents.account.passwordHint')}
            rules={[
              { required: true, message: t('agents.account.passwordRequired') },
              // ⚠️ 72 — serverdagi chegara (`CreateUserCommand`). Panelning
              // boshqa formasi 128 gacha ruxsat berardi va uzun parol
              // BIRINCHI qadamdan keyin rad etilardi: tugun yaratilib,
              // hisob yaratilmay qolardi.
              { min: 12, max: 72, message: t('agents.account.passwordLength') },
            ]}
          >
            <Input.Password visibilityToggle={false} autoComplete="new-password" />
          </Form.Item>
        </>
      ) : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agents.account.noUserPermission')}
        />
      )}

      <Typography.Text strong>{t('agents.account.nodeTitle')}</Typography.Text>
      <Divider style={{ margin: '8px 0 16px' }} />

      <Form.Item
        name="code"
        label={t('agents.code')}
        extra={t('agents.account.codeHint')}
        rules={[
          { required: true, message: t('agents.codeRequired') },
          { pattern: /^[a-zA-Z0-9._-]{2,64}$/, message: t('agents.codeFormat') },
        ]}
      >
        <Input autoComplete="off" />
      </Form.Item>

      {/* ⚠️ Ota tugun ENDI OSHKORA so'raladi. Ilgari u ro'yxatda qaysi
          qator tanlanganidan JIMGINA olinardi: operator agent qo'shmoqchi
          bo'lib, bexosdan tanlangan qator ostiga tushib qolardi va buni
          faqat daraxtda ko'rib sezardi. */}
      <Form.Item
        name="parentId"
        label={t('agents.account.parent')}
        extra={t('agents.account.parentHint')}
      >
        <AgentSelect placeholder={t('agents.account.parentPlaceholder')} />
      </Form.Item>

      <Form.Item
        name="settlementMode"
        label={t('agents.settlementMode')}
        extra={t('agents.settlementHint')}
      >
        <Select
          options={SETTLEMENT_MODES.map((m) => ({ value: m, label: t(`settlementMode.${m}`) }))}
        />
      </Form.Item>

      {canFund ? (
        <>
          <Typography.Text strong>{t('agents.account.moneyTitle')}</Typography.Text>
          <Divider style={{ margin: '8px 0 16px' }} />

          <Form.Item
            name="initialDeposit"
            label={t('agents.account.initialDeposit')}
            extra={t('agents.account.initialDepositHint')}
          >
            <MoneyInput currency={currency} minMinor={0} />
          </Form.Item>
          <Form.Item
            name="debtLimit"
            label={t('agents.debtLimit')}
            extra={t('agents.debtLimitHint')}
          >
            <MoneyInput currency={currency} minMinor={0} />
          </Form.Item>

          {/* ⚠️ Bu PUL limiti (`FundLimitKind`), abonent amallari
              chegarasi EMAS. Bo'sh qoldirilsa — cheksiz, nol emas:
              nol "umuman ruxsat yo'q" degani va u agentni jimgina
              ishlay olmaydigan holatga qo'yardi. */}
          {/* ⚠️ BITTA TO'LOV shifti — kunlik/oylikdan boshqa savolga
              javob beradi va u yerda «sarflandi» degan hisob YO'Q.
              Sukut 100 000 — egasi bergan aniq raqam (2026-08-19). */}
          <Form.Item
            name="perTxnLimit"
            label={t('agents.account.perTxnLimit')}
            extra={t('agents.account.perTxnHint')}
          >
            <MoneyInput currency={currency} minMinor={0} />
          </Form.Item>
          <Form.Item
            name="dailyLimit"
            label={t('agents.account.dailyLimit')}
            extra={t('agents.account.fundLimitHint')}
          >
            <MoneyInput currency={currency} minMinor={0} />
          </Form.Item>
          <Form.Item name="monthlyLimit" label={t('agents.account.monthlyLimit')}>
            <MoneyInput currency={currency} minMinor={0} />
          </Form.Item>
        </>
      ) : (
        <Alert type="info" showIcon message={t('agents.account.noMoneyPermission')} />
      )}
    </>
  )
}
